import {
  HotelMapFullscreen as IntelligenceHotelMapFullscreen,
  type HotelMapView,
  type HotelOfferView,
  type HotelResultsCopy,
  type HotelResultsFilterValue,
  type HotelSearchBarProps,
} from '@voll-intelligence/intelligence-ui-kit';
import type { GenUiDispatch } from '../../../shared/ui/action.js';
import type { GenUiConfig } from '../../../shared/ui/config.js';
import { genUiI18n } from '../../../shared/ui/i18n.js';
import type { ChatUiHotelOffer, HotelPlannerPlace } from '@voll-intelligence/genui-contract';
import { filterableHotelOffers, hotelResultsFiltersCopy } from './hotel_results_filter_view.js';
import { hotelMapFullscreenCopy } from './hotel_results_page_copy.js';

/** O que o mapa cheio lê do host, por prop: o locale e o "buscar nesta área"
 *  (#3114) — o mesmo port que a página de resultados lê; sem ele o kit nem
 *  assina o gesto e o botão nunca arma no mapa cheio (#3618). */
export type HotelMapFullscreenHost = Pick<GenUiConfig, 'locale' | 'hotelSearchArea'>;

type HotelMapFullscreenProps = {
  readonly host: HotelMapFullscreenHost;
  readonly map: HotelMapView;
  readonly offers: readonly HotelOfferView[];
  readonly sourceOffers: readonly ChatUiHotelOffer[];
  readonly nights?: number;
  readonly place?: HotelPlannerPlace;
  readonly copy: HotelResultsCopy;
  readonly search: HotelSearchBarProps;
  /** Filtros CONTROLADOS pelo dono da lista (`useHotelPanelList`): a mesma
   *  fonte da lista cheia, para o corte viajar lista↔mapa. */
  readonly filters: HotelResultsFilterValue;
  readonly onFiltersChange: (filters: HotelResultsFilterValue) => void;
  readonly selectedHotelId?: string;
  readonly locked: boolean;
  /** A paginação da MESMA lista (`useHotelResultsPage`, via `list.page`): o que
   *  o botão flutuante do mapa carregar aparece também no painel — estado um só,
   *  nada duplicado aqui. Opt-in: sem `onLoadMore` o controle não é montado. */
  readonly hasMore?: boolean;
  readonly loadingMore?: boolean;
  readonly onLoadMore?: () => void;
  /** A identidade do conjunto paginado (`list.page.requestKey`): quando muda,
   *  o aviso de "todos carregados" do mapa é descartado. */
  readonly loadMoreResetToken?: string;
  /** Fornecedor com mais hotéis do que os carregados — repassado à coluna de
   *  filtros (`HotelResultsFilters.countsPartial`). */
  readonly countsPartial?: boolean;
  /** A coleta inicial da lista está no ar — o "buscar nesta área" não arma
   *  (mesma régua da página de resultados). */
  readonly searchAreaPending?: boolean;
  /** Optional: sem `dispatch`, o composer de comando não é montado (o
   *  `IntelligenceHotelMapFullscreen` já trata `onCommandSubmit` ausente). */
  readonly dispatch?: GenUiDispatch;
  readonly onClose: () => void;
  readonly onOpenDetails: (hotelId: string) => void;
};

export function HotelMapFullscreen({
  host,
  map,
  offers,
  sourceOffers,
  nights,
  place,
  copy,
  search,
  filters,
  onFiltersChange,
  selectedHotelId,
  locked,
  hasMore,
  loadingMore,
  onLoadMore,
  loadMoreResetToken,
  countsPartial,
  searchAreaPending,
  dispatch,
  onClose,
  onOpenDetails,
}: Readonly<HotelMapFullscreenProps>) {
  const { locale, t } = genUiI18n(host.locale);
  const { hotelSearchArea } = host;
  const currency = sourceOffers.find(({ price }) => price)?.price?.currency ?? 'BRL';

  return (
    <IntelligenceHotelMapFullscreen
      map={map}
      offers={filterableHotelOffers(offers, sourceOffers, nights, place, currency)}
      currency={currency}
      resultsCopy={copy}
      copy={hotelMapFullscreenCopy(locale)}
      filtersCopy={hotelResultsFiltersCopy(t, locale)}
      search={search}
      filters={filters}
      onFiltersChange={onFiltersChange}
      {...(selectedHotelId ? { selectedHotelId } : {})}
      locked={locked}
      {...(hasMore === undefined ? {} : { hasMore })}
      {...(loadingMore === undefined ? {} : { loadingMore })}
      {...(onLoadMore ? { onLoadMore } : {})}
      {...(loadMoreResetToken === undefined ? {} : { loadMoreResetToken })}
      {...(countsPartial === undefined ? {} : { countsPartial })}
      {...(hotelSearchArea ? { onSearchArea: hotelSearchArea } : {})}
      {...(searchAreaPending === undefined ? {} : { searchAreaPending })}
      onClose={onClose}
      onOpenDetails={onOpenDetails}
      {...(dispatch
        ? {
            onCommandSubmit: (text: string) => {
              dispatch({ type: 'reply', text });
            },
          }
        : {})}
    />
  );
}
